import Phaser from 'phaser';

export interface GameSceneConfig {
  key: string;
  gameId: string;
  title: string;
}

export interface GameState {
  score: number;
  level: number;
  lives: number;
  isPlaying: boolean;
  isPaused: boolean;
  isGameOver: boolean;
}

/**
 * Base class for all game scenes.
 * Provides common functionality like scoring, pause, game over handling.
 */
export abstract class BaseGameScene extends Phaser.Scene {
  protected gameId: string;
  protected gameTitle: string;
  protected state: GameState;

  // Event callbacks
  public onScoreChange?: (score: number) => void;
  public onGameOver?: (finalScore: number) => void;
  public onPause?: (isPaused: boolean) => void;

  constructor(config: GameSceneConfig) {
    super({ key: config.key });
    this.gameId = config.gameId;
    this.gameTitle = config.title;
    this.state = this.getInitialState();
  }

  protected getInitialState(): GameState {
    return {
      score: 0,
      level: 1,
      lives: 3,
      isPlaying: false,
      isPaused: false,
      isGameOver: false,
    };
  }

  // Lifecycle methods to be implemented by subclasses
  abstract preload(): void;
  abstract create(): void;
  abstract update(time: number, delta: number): void;

  /**
   * Initialize the game scene
   */
  init(): void {
    this.state = this.getInitialState();

    // Set up pause handling
    this.input.keyboard?.on('keydown-P', this.togglePause, this);
    this.input.keyboard?.on('keydown-ESC', this.togglePause, this);

    // Handle visibility change (tab switch)
    this.game.events.on('blur', this.handleBlur, this);
    this.game.events.on('focus', this.handleFocus, this);
  }

  /**
   * Start the game
   */
  protected startGame(): void {
    this.state.isPlaying = true;
    this.state.isGameOver = false;
    this.state.score = 0;
    this.state.level = 1;
    this.state.lives = this.getInitialState().lives;
  }

  /**
   * Add to the score
   */
  protected addScore(points: number): void {
    this.state.score += points;
    this.onScoreChange?.(this.state.score);
  }

  /**
   * Set the score directly
   */
  protected setScore(score: number): void {
    this.state.score = score;
    this.onScoreChange?.(this.state.score);
  }

  /**
   * Lose a life
   */
  protected loseLife(): void {
    this.state.lives--;
    if (this.state.lives <= 0) {
      this.gameOver();
    }
  }

  /**
   * Level up
   */
  protected levelUp(): void {
    this.state.level++;
  }

  /**
   * Toggle pause state
   */
  protected togglePause(): void {
    if (this.state.isGameOver) return;

    this.state.isPaused = !this.state.isPaused;

    if (this.state.isPaused) {
      this.scene.pause();
      this.showPauseOverlay();
    } else {
      this.scene.resume();
      this.hidePauseOverlay();
    }

    this.onPause?.(this.state.isPaused);
  }

  /**
   * Handle game over
   */
  protected gameOver(): void {
    this.state.isPlaying = false;
    this.state.isGameOver = true;
    this.onGameOver?.(this.state.score);
    this.showGameOverOverlay();
  }

  /**
   * Restart the game
   */
  protected restart(): void {
    this.scene.restart();
  }

  // Overlay methods - can be overridden for custom visuals
  protected showPauseOverlay(): void {
    // Default: show simple pause text
    const { width, height } = this.scale;
    const overlay = this.add
      .rectangle(width / 2, height / 2, width, height, 0x000000, 0.7)
      .setDepth(1000);
    const text = this.add
      .text(width / 2, height / 2, 'PAUSED\n\nPress P or ESC to resume', {
        fontSize: '32px',
        color: '#ffffff',
        align: 'center',
      })
      .setOrigin(0.5)
      .setDepth(1001);

    // Store references for removal
    this.data.set('pauseOverlay', overlay);
    this.data.set('pauseText', text);
  }

  protected hidePauseOverlay(): void {
    const overlay = this.data.get(
      'pauseOverlay'
    ) as Phaser.GameObjects.Rectangle;
    const text = this.data.get('pauseText') as Phaser.GameObjects.Text;
    overlay?.destroy();
    text?.destroy();
  }

  protected showGameOverOverlay(): void {
    const { width, height } = this.scale;
    this.add
      .rectangle(width / 2, height / 2, width, height, 0x000000, 0.7)
      .setDepth(1000);
    this.add
      .text(width / 2, height / 2 - 40, 'GAME OVER', {
        fontSize: '48px',
        color: '#ef4444',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(1001);
    this.add
      .text(
        width / 2,
        height / 2 + 20,
        `Score: ${this.state.score.toLocaleString()}`,
        {
          fontSize: '24px',
          color: '#ffffff',
        }
      )
      .setOrigin(0.5)
      .setDepth(1001);
    this.add
      .text(width / 2, height / 2 + 70, 'Press SPACE to play again', {
        fontSize: '18px',
        color: '#94a3b8',
      })
      .setOrigin(0.5)
      .setDepth(1001);

    // Listen for restart
    this.input.keyboard?.once('keydown-SPACE', this.restart, this);
  }

  // Visibility handling
  private handleBlur(): void {
    if (this.state.isPlaying && !this.state.isPaused) {
      this.togglePause();
    }
  }

  private handleFocus(): void {
    // Optionally resume on focus
  }

  // Cleanup
  shutdown(): void {
    this.game.events.off('blur', this.handleBlur, this);
    this.game.events.off('focus', this.handleFocus, this);
  }
}
